import { AlertTriangle, ShoppingCart, UserCircle, Wallet } from 'lucide-react';
import { ORDER_STATUSES } from '../../pages/OrderDetails';

interface OrderHeaderProps {
  order: any;
  status: string;
  onUpdateStatus?: (newStatus: string) => void;
  onCancel?: () => void;
  updating?: boolean;
}

export const PAYMENT_METHODS = {
  bank_transfer: 'Transferência Bancária',
  cash_on_delivery: 'À Cobrança',
  manual: 'Manual',
  mbway: 'MB WAY',
  wallet: 'Saldo Wallet',
  multibanco: 'Multibanco'
} as const;

const OrderHeader: React.FC<OrderHeaderProps> = ({ 
  order,
  status,
  onUpdateStatus,
  onCancel,
  updating
}) => {
  const getOrderTitle = () => {
    if (order?.source === 'woocommerce') return 'Encomenda WOOCOMMERCE (Loja)';
    if (order?.isOnlineSale || order?.source === 'marketplace' || order?.isMarketplace) return 'Encomenda MARKETPLACE';
    if (order?.isPrivateSale) return 'Venda Privada';
    if (order?.isManual) return 'Encomenda MANUAL';
    return 'Encomenda MANUAL';
  };

  const getPaymentMethodLabel = (method: string) => {
    return PAYMENT_METHODS[method as keyof typeof PAYMENT_METHODS] || method;
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'cancelled':
      case 'canceled':
      case 'rejected':
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'completed':
      case 'delivered':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'processing':
      case 'preparation':
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
      case 'awaiting_payment':
      case 'awaitingstock':
      case 'awaitingpickup':
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    // Primeiro normaliza o status para o formato correto
    const normalizedStatus = status?.toLowerCase()
      .replace(/[-_\s]/g, '') // Remove hífens, underscores e espaços
      .trim();
    
    // Procura a chave correspondente no ORDER_STATUSES
    const matchingKey = Object.keys(ORDER_STATUSES).find(key => 
      key.toLowerCase().replace(/[-_\s]/g, '') === normalizedStatus
    );
    
    return matchingKey ? ORDER_STATUSES[matchingKey as keyof typeof ORDER_STATUSES] : status;
  };

  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        {getOrderTitle()}
      </h2>
      {order?.hasCancellationRequest ? (
        <div className="bg-red-100 text-red-800 px-4 py-2 rounded-lg mb-4 flex items-center gap-2">
          <AlertTriangle size={20} />
          <span>Pedido de cancelamento pendente</span>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-2">
          <ShoppingCart className="text-gray-400" size={20} />
          <div>
            <p className="text-sm text-gray-600">Número do Pedido</p>
            <p className="font-medium">{order?.orderNumber || 'N/A'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Wallet className="text-gray-400" size={20} />
          <div>
            <p className="text-sm text-gray-600">Método de Pagamento</p>
            <p className="font-medium">{getPaymentMethodLabel(order?.paymentMethod || '')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <UserCircle className="text-gray-400" size={20} />
          <div>
            <p className="text-sm text-gray-600">Estado</p>
            <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
              {getStatusLabel(status)}
            </span>
          </div>
        </div>
      </div>
      {status !== 'cancelled' && (
        <div className="flex justify-end gap-2">
          {status === 'shipped' ? (
            <>
              <button
                onClick={() => onUpdateStatus?.('delivered')}
                className="inline-flex items-center px-4 py-2 bg-[#1B2B4B] text-white text-sm rounded hover:bg-[#162139]"
                disabled={updating}
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" fill="currentColor"/>
                </svg>
                Entregue
              </button>
              <button
                onClick={() => onUpdateStatus?.('returned')}
                className="inline-flex items-center px-4 py-2 bg-[#1B2B4B] text-white text-sm rounded hover:bg-[#162139]"
                disabled={updating}
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 10h12v4H4v-4zm2 8h12v-4h2v5a1 1 0 01-1 1H7a1 1 0 01-1-1v-5h2v4zm12-16H6a1 1 0 00-1 1v5h2V6h12v4h2V5a1 1 0 00-1-1z" fill="currentColor"/>
                </svg>
                Devolvido
              </button>
            </>
          ) : (
            <>
              {status !== 'preparation' && status !== 'awaitingPickup' && (
                <button
                  onClick={() => onUpdateStatus?.('preparation')}
                  className="inline-flex items-center px-4 py-2 bg-[#1B2B4B] text-white text-sm rounded hover:bg-[#162139]"
                  disabled={updating}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 8H4V16H20V8ZM22 8V16H24V8H22ZM14 19H10V22H14V19ZM16 19H20V22H16V19ZM4 19H8V22H4V19ZM2 19H0V16H2V19ZM2 8H0V11H2V8Z" fill="currentColor"/>
                  </svg>
                  Em preparação
                </button>
              )}
              {status === 'preparation' && (
                <button
                  onClick={() => onUpdateStatus?.('awaitingPickup')}
                  className="inline-flex items-center px-4 py-2 bg-[#1B2B4B] text-white text-sm rounded hover:bg-[#162139]"
                  disabled={updating}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 8h-1V3H6v5H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zM8 5h8v3H8V5zm8 14H8v-4h8v4zm4-4h-2v-2H6v2H4v-4c0-.55.45-1 1-1h14c.55 0 1 .45 1 1v4z" fill="currentColor"/>
                  </svg>
                  Aguardar Recolha
                </button>
              )}
              {status === 'awaitingPickup' && (
                <button
                  onClick={() => onUpdateStatus?.('shipped')}
                  className="inline-flex items-center px-4 py-2 bg-[#1B2B4B] text-white text-sm rounded hover:bg-[#162139]"
                  disabled={updating}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="currentColor"/>
                  </svg>
                  Enviado
                </button>
              )}
              <button
                onClick={onCancel}
                className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded"
                disabled={updating}
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
                </svg>
                Cancelado
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderHeader;